import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  EventEmitter,
  HostListener,
  Input,
  IterableDiffers,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChipComponent } from '../chip/chip.component';
import { TabViewModule } from 'primeng/tabview';
import { FieldsetModule } from 'primeng/fieldset';
import { AccordionModule } from 'primeng/accordion';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CommentDetailCardComponent } from '../comment-detail-card/comment-detail-card.component';
import { CommentDialogComponent } from '../comment-dialog/comment-dialog.component';
import { ButtonComponent } from '../../button/button.component';
import { NotificationCardComponent } from '../notification-card/notification-card.component';
import { FileService } from '../file-services/file.service';
import { AggregateResponse } from '../../../models/aggregates/aggregate-response.model';
import { AggregateType, AggregateTypeLabels } from '../../../models/aggregates/aggregate-type.enum';
import { CommentType } from '../../../models/comments/comments-type.enum';
import { AggregateReportState } from '../../../models/status/aggregate-report-state.enum';
import { OrderDetailResponse } from '../../../models/order/order-detail-response.model';
import { OrderStatus } from '../../../models/status/order-status.enum';
import { AggregateInspectionStatus } from '../../../models/status/aggregate-inspection-status.enum';
import { RemuneratedTaskHygieneAggregateType } from '../../../models/aggregates/remunerate-tasks-hygiene-type.enum';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-detail-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    TabViewModule,
    FieldsetModule,
    AccordionModule,
    ProgressSpinnerModule,
    ChipComponent,
    CommentDetailCardComponent,
    CommentDialogComponent,
    ButtonComponent,
    NotificationCardComponent,
  ],
  templateUrl: './detail-table.component.html',
  styleUrls: ['./detail-table.component.scss'],
})
export class DetailTableComponent implements OnInit, DoCheck {
  @Input() orderDetail: OrderDetailResponse | undefined;
  @Input() aggregates: AggregateResponse[] = [];
  @Output() requestParentReload = new EventEmitter<void>();
  @ViewChild(CommentDialogComponent) commentDialogComponent!: CommentDialogComponent;

  remuneratedTaskTypeLabels = RemuneratedTaskHygieneAggregateType;
  sortedData: AggregateResponse[] = this.aggregates;
  sortColumn = '';
  sortDirection: 'asc' | 'desc' | '' = '';
  OrderStatus = OrderStatus;
  AggregateReportState = AggregateReportState;
  error: Error | null = null;
  commentType = CommentType;
  showComment: Record<string, boolean> = {};
  activeTabs: Record<string, string> = {};
  showDetails: Record<string, boolean> = {};
  isDownloadingReport: Record<string, boolean> = {};
  columns = [
    { key: 'idrId', label: 'IDR-ID', align: 'left' },
    { key: 'name', label: 'Anlagenname', align: 'left' },
    { key: 'type', label: 'Typ', align: 'left' },
    { key: 'buildingName', label: 'Aufstellungsort', align: 'left' },
    { key: 'customerProvidedId', label: 'Kunden-ID', align: 'left' },
    { key: 'inspectionDate', label: 'Insp.-Datum', align: 'left' },
    { key: 'inspectionStatus', label: 'Insp.-Status', align: 'center' },
    { key: 'aggregateReport.reportId', label: 'Bericht', align: 'center' },
  ];
  protected readonly AggregateInspectionStatus = AggregateInspectionStatus;
  private aggregatesDiffer: any;
  private openDropdownId: number | null = null;

  constructor(
    private fileService: FileService,
    private iterableDiffers: IterableDiffers,
    private appRef: ApplicationRef
  ) {
    this.aggregatesDiffer = this.iterableDiffers.find([]).create();
  }

  ngOnInit(): void {
    this.updateData();
  }

  ngDoCheck(): void {
    const changes = this.aggregatesDiffer.diff(this.aggregates);
    if (changes) {
      this.updateAggregateArray();
      this.updateData();
    }
  }

  updateAggregateArray(): void {
    this.aggregates = [...this.aggregates];
  }

  toggleComment(id: string): void {
    this.showComment[id] = !this.showComment[id];
    if (!this.activeTabs[id]) {
      this.activeTabs[id] = 'comments';
    }
    if (this.showDetails[id]) {
      this.showDetails[id] = false;
    }
  }

  toggleDetails(aggregate: AggregateResponse): void {
    if (this.hasCancelReasonHygiene(aggregate) || this.hasValidTasks(aggregate)) {
      this.showDetails[aggregate.id] = !this.showDetails[aggregate.id];
      if (this.showComment[aggregate.id]) {
        this.showComment[aggregate.id] = false;
      }
    }
  }

  getCommentImage(aggregate: AggregateResponse): string {
    return aggregate.noteInternalBackoffice ||
    aggregate.noteInternalInspector ||
    aggregate.hygiene?.noteExternalReport ||
    aggregate.hygiene?.noteInternalBackoffice ||
    aggregate.hygiene?.noteInternalInspector ||
    aggregate.energetic?.noteExternalReport ||
    aggregate.energetic?.noteInternalBackoffice ||
    aggregate.energetic?.noteInternalInspector
      ? '/assets/comment.svg'
      : '/assets/no_comment.svg';
  }

  getInfoImage(aggregate: AggregateResponse): string {
    return this.hasCancelReasonHygiene(aggregate) || this.hasValidTasks(aggregate)
      ? '/assets/info.svg'
      : '/assets/no_info.svg';
  }

  downloadSingleReport(aggregateId: string): void {
    if (this.isDownloadingReport[aggregateId]) return;
    this.isDownloadingReport[aggregateId] = true;
    this.fileService
      .downloadSingleHygieneAggregateReport(aggregateId)
      .pipe(finalize(() => (this.isDownloadingReport[aggregateId] = false)))
      .subscribe((response) => {
        this.handleFileDownload(response);
      });
  }

  updateData(): void {
    const sortedData = [...this.aggregates];
    if (this.sortColumn) {
      sortedData.sort((a, b) => {
        const aVal = this.getNestedValue(a, this.sortColumn);
        const bVal = this.getNestedValue(b, this.sortColumn);
        const aValue = aVal !== null && aVal !== undefined ? aVal : '';
        const bValue = bVal !== null && bVal !== undefined ? bVal : '';
        if (aValue === '' && bValue !== '') return 1;
        if (aValue !== '' && bValue === '') return -1;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return this.sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        return 0;
      });
    }
    this.sortedData = sortedData;
  }

  setSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection =
        this.sortDirection === 'asc'
          ? 'desc'
          : this.sortDirection === 'desc'
            ? ''
            : 'asc';
      if (this.sortDirection === '') {
        this.sortColumn = '';
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.updateData();
  }

  getLabel(type: AggregateType): string {
    return AggregateTypeLabels[type];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdowns = document.querySelectorAll('.dropdown');
    dropdowns.forEach((dropdown) => {
      const button = dropdown.querySelector('.dropdown-toggle') as HTMLElement;
      const menu = dropdown.querySelector('.dropdown-menu') as HTMLElement;
      if (button && menu && !button.contains(target) && !menu.contains(target)) {
        this.openDropdownId = null;
      }
    });
  }

  openCommentDialog(id: string, comment: string | undefined, type: string): void {
    this.commentDialogComponent.showDialog(id, comment, type);
  }

  updateDetailOnChange(): void {
    this.requestParentReload.emit();
  }

  getTaskLabel(taskType: string): string {
    return (
      this.remuneratedTaskTypeLabels[
        taskType as keyof typeof this.remuneratedTaskTypeLabels
        ] || taskType
    );
  }

  hasValidTasks(aggregate: AggregateResponse): boolean {
    return (
      aggregate.hygiene?.remuneratedTasks?.some((task) => task.type?.trim()) ||
      false
    );
  }

  hasCancelReasonHygiene(aggregate: AggregateResponse): boolean {
    return !!(
      aggregate.hygiene?.cancelReason ||
      aggregate.hygiene?.cancelReasonComment
    );
  }

  hasCancelReasonEnergetic(aggregate: AggregateResponse): boolean {
    return !!(
      aggregate.energetic?.cancelReason ||
      aggregate.energetic?.cancelReasonComment
    );
  }

  getInspectionDate(aggregate: AggregateResponse): string | undefined {
    return aggregate.hygiene?.inspectionDate
  }

  getInspectionStatus(
    aggregate: AggregateResponse
  ): AggregateInspectionStatus | undefined {
    return aggregate.hygiene?.status
  }

  private handleFileDownload(response: any): void {
    const contentDispositionHeader = response.headers.get('Content-Disposition') || '';
    const fileName = this.fileService.extractFilename(contentDispositionHeader);
    const blob = response.body;
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }

  private getNestedValue(obj: any, path: string) {
    if (path === 'inspectionDate') {
      return obj.hygiene?.inspectionDate || obj.energetic?.inspectionDate;
    }

    if (path === 'inspectionStatus') {
      return obj.hygiene?.status || obj.energetic?.status;
    }

    return path.split('.').reduce((o, p) => (o ? o[p] : undefined), obj);
  }
}
